import i18n from 'i18next';

import { PromiseAll } from 'api/common';
import { getCurrentUtcDTStr } from 'utils/dateTime';
import { notQualifiedTypes } from 'config/notQualifiedTypes';
import {
    createNewEntry,
    normalizeEntryPayload
} from 'client/hooks/useInitPromo/userEntryState/getUserEntry';
import { suspendPromo } from 'client/redux/modules/promoState';
import {
    showPromoNotAvailableErrDialog,
    showCustomErrDialog,
    showPromoUnavailableCloseDialog
} from 'client/redux/modules/systemErrorDialog';
import checkEntryNeedPoint from 'client/hooks/useInitPromo/userEntryState/checkEntryNeedPoint';
import checkEntryNeedPasscode from 'client/utils/passcode/checkEntryNeedPasscode';
import {
    setGameNotReadyToPlay,
    clearCurrentPrizeUUID,
    clearCurrentUnanimationEntriesUUID
} from 'client/redux/modules/purePrizeState';
import {
    getLotteryConfig,
    lotteryCategories
} from 'editor/components/SideBar/Prize/configs/prizeTypeConfig';
import { lotteryInstantTypes } from 'config/promoLotteryTypes';

const defaultState = {
    currentEntryId: 0,
    currentEntryUUID: '',
    hasGameResult: false,
    hasSubmitTracking: false,
    hasFilledSignupForm: false,
    // 該 Entry 是否已經配即時獎
    hasInstantPrize: false,
    // 該 Entry 是否已經配參加獎
    hasParticipantPrize: false,
    // 該 Entry 是否已填入邀請碼
    hasReferralCodeInput: false,
    hasPointReduced: false,
    isNeedReducePoint: false,
    // 是否一定要填入邀請碼
    isNeedEnterReferralCode: false,
    isNeedEnterPasscode: false,
    isEntryOverdue: false,
    // 是否可以填入邀請碼
    isCanEnterReferralCode: false,
    createEntryNotQualifiedType: '',
    gameResultCreatedAt: ''
};

const UPDATE_USER_ENTRY_STATE = 'userEntryState/UPDATE_USER_ENTRY_STATE';

const reducer = (state = defaultState, action) => {
    switch (action.type) {
        case UPDATE_USER_ENTRY_STATE:
            return { ...state, ...action.payload };
        default:
            return state;
    }
};

export default reducer;

export function setCurrentEntriesUUID(currentEntryUUID) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            currentEntryUUID
        }
    };
}

export function successPlayGame() {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            hasGameResult: true,
            hasSubmitTracking: true,
            gameResultCreatedAt: getCurrentUtcDTStr()
        }
    };
}

export function replayGame() {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            hasGameResult: false
        }
    };
}

export function successFillSignUpForm() {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            hasFilledSignupForm: true
        }
    };
}

// 通知 user entry，此次 entry 已成功配獎
export function successGetWinner(type, participantType) {
    const lotteryCategory = getLotteryConfig(type).category;
    const payloadMapping = {
        [lotteryCategories.IS_INSTANT_LOTTERY]: { hasInstantPrize: true },
        [lotteryCategories.IS_PARTICIPANT_LOTTERY]: { hasParticipantPrize: true }
    };
    if (type === participantType && type === lotteryInstantTypes.JOIN_AND_GET) {
        return {
            type: UPDATE_USER_ENTRY_STATE,
            payload: { hasParticipantPrize: true }
        };
    }
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: payloadMapping[lotteryCategory]
    };
}

export function setNewEntry({
    entryId: currentEntryId,
    entryUuid: currentEntryUUID,
    ...rest
} = {}) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: { ...defaultState, currentEntryId, currentEntryUUID, ...rest }
    };
}

export function setNeedReducePoint(status) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            isNeedReducePoint: status
        }
    };
}

export function setNeedEnterPasscode(status) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            isNeedEnterPasscode: status
        }
    };
}

export function setCreateEntryNotQualifiedType(type) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            createEntryNotQualifiedType: type
        }
    };
}

export function setReferralCodeInput(hasReferralCodeInput) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            hasReferralCodeInput,
            isNeedEnterReferralCode: !hasReferralCodeInput,
            isCanEnterReferralCode: !hasReferralCodeInput
        }
    };
}

export function setEntryOverdueStatus(status = false) {
    return {
        type: UPDATE_USER_ENTRY_STATE,
        payload: {
            isEntryOverdue: status
        }
    };
}

export function setUserNewEntry(recaptchaToken) {
    return async (dispatch, getState) => {
        const {
            promo: { uuid },
            signUpForm: { id: formId, enableFilledByEntry },
            lotteryInstant: { uuid: lotteryInstantUUID },
            lotteryParticipant: { uuid: lotteryParticipantUUID },
            promoState: { isPurePrizeGame, isTestMode, reducePointMode, enablePasscodeVerify },
            userEntryState: { hasFilledSignupForm },
            mgmReferral: {
                lotteryParticipant: { uuid: mgmLotteryParticipantUUID } = {},
                lotteryInstant: { uuid: mgmLotteryInstantUUID } = {}
            }
        } = getState();
        const hasEnableSignUpForm = Boolean(formId);
        const hasEnableFilledByEntry = Boolean(enableFilledByEntry);
        const hasEnableLotteryParticipant =
            Boolean(lotteryParticipantUUID) || Boolean(mgmLotteryParticipantUUID);
        const hasEnableLotteryInstant =
            Boolean(lotteryInstantUUID) || Boolean(mgmLotteryInstantUUID);

        const {
            entry,
            isPlayTimeLimitError,
            isTotalMonthlyEntryLimitError,
            isNotValidPromoJoinTime,
            isNotValidPromoStatusToJoin,
            createEntryNotQualifiedType
        } = await createNewEntry(uuid, recaptchaToken);

        if (createEntryNotQualifiedType) {
            if (
                createEntryNotQualifiedType === notQualifiedTypes.ENTRIES.REACH_LIMIT &&
                isTestMode
            ) {
                dispatch(
                    showCustomErrDialog({
                        title: i18n.t('label.test_mode'),
                        desc: i18n.t('desc.testmode_credit_full')
                    })
                );
            } else if (
                createEntryNotQualifiedType === notQualifiedTypes.QUALIFICATION.MEMBER_ORDER
            ) {
                dispatch(
                    showCustomErrDialog({
                        title: i18n.t('label.no_qualify_join'),
                        desc: i18n.t('desc.sorry_no_qualify.name_list')
                    })
                );
            } else {
                dispatch(
                    showCustomErrDialog({
                        title: i18n.t('label.no_qualify_join'),
                        desc: i18n.t('desc.sorry_no_qualify')
                    })
                );
            }
            return false;
        } else if (isTotalMonthlyEntryLimitError || isPlayTimeLimitError) {
            dispatch(showPromoUnavailableCloseDialog());
            return false;
        } else if (isNotValidPromoJoinTime || isNotValidPromoStatusToJoin) {
            dispatch(suspendPromo());
            dispatch(showPromoNotAvailableErrDialog());
            return false;
        } else {
            const newGameEntry = normalizeEntryPayload(entry, {
                hasEnableSignUpForm,
                hasEnableLotteryInstant,
                hasEnableLotteryParticipant
            });
            dispatch(
                setNewEntry({
                    ...newGameEntry,
                    hasFilledSignupForm: hasEnableFilledByEntry ? false : hasFilledSignupForm
                })
            );

            const [isNeedReducePoint, isNeedEnterPasscode] = await PromiseAll([
                checkEntryNeedPoint({
                    promoUUID: uuid,
                    entry,
                    reducePointMode
                }),
                checkEntryNeedPasscode({
                    promoUUID: uuid,
                    entryUUID: entry.entryUuid,
                    enablePasscodeVerify
                })
            ]);
            dispatch(setNeedReducePoint(isNeedReducePoint));
            dispatch(setNeedEnterPasscode(isNeedEnterPasscode));
            if (isPurePrizeGame) {
                dispatch(setGameNotReadyToPlay());
                dispatch(clearCurrentPrizeUUID());
                dispatch(clearCurrentUnanimationEntriesUUID());
            }

            return true;
        }
    };
}
